import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, RotateCcw, Award, ShieldCheck, HeartHandshake, Clock, LifeBuoy, Loader2, LayoutGrid, PartyPopper, Lightbulb, Compass, Link2, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { METER_META, TONE_STYLES, OUTCOME_TIERS, PRINCIPLES } from "@/lib/sim";
import { RANKS, COMPETENCIES, indefinite } from "@/lib/progression";
import { RankBadge } from "@/components/sim/RankBadge";
import { CrisisFooter } from "@/components/sim/CrisisFooter";

const ICONS = { ShieldCheck, HeartHandshake, Clock, LifeBuoy };

const TIER_ACCENT = {
  good: "from-emerald-700 to-emerald-800",
  caution: "from-amber-600 to-amber-700",
  harmful: "from-rose-600 to-rose-700",
};

export const DebriefBody = ({ text }) => {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        const key = `${i}-${line.slice(0, 24)}`;
        if (line.startsWith("- ") || line.startsWith("• ")) {
          return (
            <p key={key} className="flex gap-2 text-[15px] leading-relaxed text-stone-700">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-600" />
              <span>{line.replace(/^[-•]\s*/, "")}</span>
            </p>
          );
        }
        if (line.endsWith(":")) {
          return (
            <h4 key={key} className="font-heading text-sm font-semibold uppercase tracking-wider text-emerald-800 pt-3 first:pt-0">
              {line.replace(/:$/, "")}
            </h4>
          );
        }
        return <p key={key} className="text-[15px] leading-relaxed text-stone-700">{line}</p>;
      })}
    </div>
  );
};

const FADE_UP = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
const FADE_UP_SCALE = { initial: { opacity: 0, y: 16, scale: 0.98 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { duration: 0.5 } };
const BAR_TRANSITION = { duration: 0.7, ease: "easeOut" };

const DebriefContent = ({ exploring, loadingDebrief, debrief }) => {
  if (exploring) {
    return (
      <p className="text-stone-600" data-testid="debrief-exploring-note">
        You were exploring, so nothing was recorded and there is no personal reflection.
        Enter your name on the case screen and play again to train for real.
      </p>
    );
  }
  if (loadingDebrief) {
    return (
      <div className="flex items-center gap-3 text-stone-500" data-testid="debrief-loading">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />
        <span>Your coach is reviewing the shift…</span>
      </div>
    );
  }
  if (debrief) {
    return <div data-testid="debrief-content"><DebriefBody text={debrief} /></div>;
  }
  return (
    <p className="text-stone-500" data-testid="debrief-error">
      The reflection coach is unavailable right now. Your results are still saved.
    </p>
  );
};

export const SummaryScreen = ({ caseTitle, client, meters, tierKey, endedEarly, tally, choices, caseScore, rank, promotion, debrief, loadingDebrief, sessionId, exploring = false, onReplay, onTryAnother, onHome }) => {
  const tier = OUTCOME_TIERS[tierKey];
  const accent = TIER_ACCENT[tier.tone];
  const revisit = choices.filter((c) => c.tone !== "good");

  const copyReportLink = async () => {
    const url = `${window.location.origin}/report/${sessionId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Report link copied. Share it with your supervisor.");
    } catch {
      window.prompt("Copy this report link:", url);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      {promotion && (
        <motion.div
          {...FADE_UP_SCALE}
          data-testid="promotion-banner"
          className="mb-6 rounded-3xl border border-emerald-300 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white shadow-md overflow-hidden"
        >
          <div className="px-6 sm:px-10 py-8">
            <div className="flex items-center gap-2 text-white/85 text-[11px] font-semibold uppercase tracking-[0.18em]">
              <PartyPopper className="h-4 w-4" /> Promotion earned
            </div>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
              You are now {indefinite(RANKS[promotion.newRank].title)} {RANKS[promotion.newRank].title}
            </h2>
            <p className="mt-2 text-white/90 leading-relaxed max-w-2xl">{RANKS[promotion.newRank].meaning}</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {COMPETENCIES.map((comp) => (
                <div key={comp.key} className="flex items-start gap-2 rounded-xl bg-white/10 px-3 py-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-emerald-100" />
                  <span className="text-sm text-white/90">
                    <span className="font-semibold text-white">{comp.label}.</span> {comp.note}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      <motion.div
        {...FADE_UP}
        className="rounded-3xl border border-stone-200 bg-white shadow-sm overflow-hidden"
        data-testid="summary-screen"
      >
        <div className={`bg-gradient-to-br ${accent} px-6 sm:px-10 py-10 text-white`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-white/80 text-[11px] font-semibold uppercase tracking-[0.18em]">
                <Award className="h-4 w-4" /> {caseTitle}{endedEarly ? " · ended early" : " · complete"}
              </div>
              <h2 data-testid="outcome-title" className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
                {tier.title}
              </h2>
              <p className="mt-3 text-white/90 text-base sm:text-lg leading-relaxed max-w-xl">{tier.blurb}</p>
            </div>
            {caseScore != null && (
              <div data-testid="case-score" className="shrink-0 text-center rounded-2xl bg-white/15 backdrop-blur px-5 py-4 ring-1 ring-white/25">
                <div className="text-3xl sm:text-4xl font-heading font-semibold tabular-nums">{caseScore}%</div>
                <div className="text-[11px] uppercase tracking-wider text-white/80 mt-1">Case score</div>
              </div>
            )}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">Current rank</span>
            <RankBadge rank={rank} className="ring-white/40" />
          </div>
          {endedEarly && (
            <p data-testid="early-note" className="mt-4 text-sm text-white/85 bg-white/10 rounded-xl px-4 py-2.5">
              This case ended early, so it does not count toward promotion. Give it another run when you are ready.
            </p>
          )}
        </div>

        <div className="px-6 sm:px-10 py-8">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-stone-500">Final meters</h3>
              <div className="mt-4 space-y-4">
                {["safety", "trust", "time", "resources"].map((id) => {
                  const meta = METER_META[id];
                  const Icon = ICONS[meta.icon];
                  return (
                    <div key={id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-sm font-medium text-stone-700">
                          <Icon className={`h-4 w-4 ${meta.text}`} /> {meta.label}
                        </span>
                        <span className={`text-sm font-semibold tabular-nums ${meta.text}`}>{Math.round(meters[id])}</span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-stone-200 overflow-hidden">
                        <motion.div
                          className={`h-full rounded-full ${meta.bar}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${meters[id]}%` }}
                          transition={BAR_TRANSITION}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <p data-testid="time-balance-note" className="mt-4 text-xs leading-relaxed text-stone-500 bg-stone-50 rounded-xl px-3 py-2.5">
                About the Time meter: thorough, trauma-informed care takes real minutes, and that is expected. Balancing depth with the rest of the queue is itself a skill you are building; the goal is presence without losing the whole shift.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-stone-500">Your choices</h3>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <StatCard color="text-emerald-700" bg="bg-emerald-50" value={tally.good} label="Survivor-centered" />
                <StatCard color="text-amber-700" bg="bg-amber-50" value={tally.caution} label="Mixed" />
                <StatCard color="text-rose-700" bg="bg-rose-50" value={tally.harmful} label="Harmful" />
              </div>
              <div className="mt-4 space-y-2 max-h-52 overflow-y-auto pr-1">
                {choices.map((c) => {
          const t = TONE_STYLES[c.tone];
          return (
            <div key={c.decision_id} className="flex items-start gap-2 text-sm">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${t.dot}`} />
                      <span className="text-stone-600 leading-snug">
                        <span className="font-medium text-stone-800">{c.decision_title}</span>
                        <span className="text-stone-400"> · {c.choice_text}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {revisit.length > 0 && (
            <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-6" data-testid="revisit-section">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-700" />
                <h3 className="font-heading text-lg font-semibold text-stone-900">Moments worth revisiting</h3>
              </div>
              <p className="mt-1.5 text-sm text-stone-600">
                These decisions are great places to try a more survivor-centered approach on your next run.
              </p>
              <div className="mt-4 space-y-2">
                {revisit.map((c) => {
                  const t = TONE_STYLES[c.tone];
                  return (
                    <div key={c.decision_id} className="flex items-start gap-2 text-sm">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${t.dot}`} />
                      <span className="text-stone-700 leading-snug">
                        <span className="font-medium text-stone-900">{c.decision_title}.</span>{" "}
                        <span className="text-stone-500">You chose: {c.choice_text}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {choices.some((c) => c.principle) && (
            <div className="mt-8 rounded-2xl border border-stone-200 bg-stone-50/60 p-6" data-testid="principles-section">
              <div className="flex items-center gap-2">
                <Compass className="h-5 w-5 text-stone-600" />
                <h3 className="font-heading text-lg font-semibold text-stone-900">Trauma-informed principles</h3>
              </div>
              <p className="mt-1.5 text-sm text-stone-600">
                How your choices mapped to the five principles of trauma-informed care. Each dot is one decision, colored by how survivor-centered your choice was.
              </p>
              <div className="mt-4">
                {PRINCIPLES.map((p) => {
                  const related = choices.filter((c) => c.principle === p.key);
                  if (!related.length) return null;
                  return (
                    <div key={p.key} data-testid={`principle-row-${p.key}`} className="flex items-start justify-between gap-4 py-2.5 border-b border-stone-200/70 last:border-0">
                      <div>
                        <span className="font-medium text-stone-900 text-sm">{p.label}</span>
                        <p className="text-xs text-stone-500 mt-0.5">{p.desc}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 mt-1 flex-wrap justify-end max-w-[40%]">
                        {related.map((c) => (
                          <span key={c.decision_id} title={c.decision_title} className={`h-2.5 w-2.5 rounded-full ${TONE_STYLES[c.tone].dot}`} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6" data-testid="debrief-section">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-700" />
              <h3 className="font-heading text-lg font-semibold text-stone-900">Reflection Coach</h3>
            </div>
            <div className="mt-4">
              <DebriefContent exploring={exploring} loadingDebrief={loadingDebrief} debrief={debrief} />
            </div>
            <p data-testid="debrief-disclosure" className="mt-4 text-xs text-stone-400">
              This reflection summarizes your choices against the training material. It is not clinical guidance.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="replay-button"
              onClick={onReplay}
              className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-6"
            >
              <RotateCcw className="mr-2 h-4 w-4" /> Run this case again
            </Button>
            <Button
              data-testid="try-another-button"
              variant="outline"
              onClick={onTryAnother}
              className="rounded-full border-stone-300 text-stone-800 hover:bg-stone-50 h-11 px-6"
            >
              <LayoutGrid className="mr-2 h-4 w-4" /> Try another case
            </Button>
            <Button
              data-testid="back-home-button"
              variant="outline"
              onClick={onHome}
              className="rounded-full border-stone-300 text-stone-800 hover:bg-stone-50 h-11 px-6"
            >
              <Home className="mr-2 h-4 w-4" /> Back to main screen
            </Button>
            {sessionId && (
              <Button
                data-testid="share-report-button"
                variant="outline"
                onClick={copyReportLink}
                className="rounded-full border-emerald-300 text-emerald-800 hover:bg-emerald-50 h-11 px-6"
              >
                <Link2 className="mr-2 h-4 w-4" /> Copy report link
              </Button>
            )}
          </div>

          <CrisisFooter className="mt-10" />
        </div>
      </motion.div>
    </div>
  );
};

const StatCard = ({ color, bg, value, label }) => (
  <div className={`rounded-xl ${bg} p-3 text-center`}>
    <div className={`text-2xl font-heading font-semibold ${color}`}>{value}</div>
    <div className="text-[11px] leading-tight text-stone-500 mt-1">{label}</div>
  </div>
);
